import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiMapPin, FiCreditCard, FiDollarSign, FiUser, FiPhone, FiTag, FiCheck, FiX } from 'react-icons/fi';
import api from '@/services/api';
import { useCart } from '@/contexts/CartContext';
import { formatCurrency } from '@/utils/formatters';
import LoadingSpinner from '@/components/common/LoadingSpinner';
import CepInput from '@/components/common/CepInput';
import { CpfInput, PhoneInput, validateCPF, validatePhone } from '@/components/common/MaskedInputs';

const Checkout = () => {
  const { storeId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, clearStoreCart, getCartTotal } = useCart();
  
  const [store, setStore] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState('pix');
  
  // Dados do cliente
  const [customerName, setCustomerName] = useState('');
  const [customerCpf, setCustomerCpf] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const [cpfError, setCpfError] = useState('');
  const [phoneError, setPhoneError] = useState('');
  
  // Endereço
  const [deliveryAddress, setDeliveryAddress] = useState('');
  const [addressNumber, setAddressNumber] = useState('');
  const [addressComplement, setAddressComplement] = useState('');
  const [cepValidated, setCepValidated] = useState(false);
  const [location2, setLocation2] = useState(null);
  
  const [deliveryFee, setDeliveryFee] = useState(15);
  const [error, setError] = useState('');
  
  // Cupom de frete
  const [couponCode, setCouponCode] = useState('');
  const [couponApplied, setCouponApplied] = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [couponError, setCouponError] = useState('');
  const [deliveryDiscount, setDeliveryDiscount] = useState(0);

  const storeCart = cart[storeId];
  const subtotal = getCartTotal(storeId);

  useEffect(() => {
    if (!storeCart) {
      navigate('/cliente/carrinho');
      return;
    }
    loadStore();
    
    // Verificar se veio cupom pela URL
    const params = new URLSearchParams(location.search);
    const urlCoupon = params.get('cupom');
    if (urlCoupon) {
      setCouponCode(urlCoupon.toUpperCase());
      // Auto-validar o cupom
      setTimeout(() => validateCoupon(urlCoupon), 500);
    }
  }, [storeId]);

  const handleAddressFound = (addressData) => {
    setDeliveryAddress(addressData.fullAddress);
    setCepValidated(true);
    setLocation2({
      lat: -23.5114,
      lng: -46.8763
    });
  };

  const loadStore = async () => {
    try {
      const response = await api.get(`/stores/${storeId}`);
      setStore(response.data);
      setDeliveryFee(15);
    } catch (error) {
      console.error('Error loading store:', error);
    } finally {
      setLoading(false);
    }
  };

  const validateCoupon = async (code = couponCode) => {
    if (!code.trim()) {
      setCouponError('Digite um código de cupom');
      return;
    }

    setCouponLoading(true);
    setCouponError('');

    try {
      const response = await api.post('/coupons/freight/validate', {
        code: code.trim().toUpperCase(),
        store_id: storeId
      });

      setCouponApplied(response.data);
      setDeliveryDiscount(response.data.discount_amount);
      setCouponError('');
    } catch (error) {
      setCouponError(error.response?.data?.detail || 'Cupom inválido');
      setCouponApplied(null);
      setDeliveryDiscount(0);
    } finally {
      setCouponLoading(false);
    }
  };

  const removeCoupon = () => {
    setCouponApplied(null);
    setCouponCode('');
    setDeliveryDiscount(0);
    setCouponError('');
  };

  const validateForm = () => {
    let isValid = true;
    
    // Validar nome
    if (!customerName.trim() || customerName.trim().split(' ').length < 2) {
      setError('Informe seu nome completo (nome e sobrenome)');
      isValid = false;
      return isValid;
    }
    
    // Validar CPF
    if (!validateCPF(customerCpf)) {
      setCpfError('CPF inválido');
      isValid = false;
    } else {
      setCpfError('');
    }
    
    // Validar telefone
    if (!validatePhone(customerPhone)) {
      setPhoneError('Telefone inválido');
      isValid = false;
    } else {
      setPhoneError('');
    }
    
    // Validar CEP
    if (!cepValidated) {
      setError('Busque e valide o CEP primeiro');
      isValid = false;
      return isValid;
    }
    
    // Validar número
    if (!addressNumber.trim()) {
      setError('Informe o número do endereço');
      isValid = false;
      return isValid;
    }
    
    if (isValid) setError('');
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setSubmitting(true);
    setError('');

    // Monta endereço completo
    let fullAddress = deliveryAddress;
    fullAddress = fullAddress.replace(/,/, `, ${addressNumber},`);
    if (!fullAddress.includes(addressNumber)) {
      fullAddress = `${fullAddress}, Nº ${addressNumber}`;
    }
    if (addressComplement) {
      fullAddress = `${fullAddress} - ${addressComplement}`;
    }

    try {
      const orderData = {
        store_id: storeId,
        items: storeCart.items.map(item => ({
          product_id: item.product.id,
          quantity: item.quantity,
          selected_color: item.selectedColor || null,
          selected_size: item.selectedSize || null
        })),
        delivery_address: fullAddress,
        delivery_coordinates: location2,
        payment_method: paymentMethod,
        customer_name: customerName.trim(),
        customer_cpf: customerCpf.replace(/[^\d]/g, ''),
        customer_phone: customerPhone.replace(/[^\d]/g, ''),
        freight_coupon_code: couponApplied ? couponApplied.code : null
      };

      const response = await api.post('/orders', orderData);
      clearStoreCart(storeId);
      
      if (paymentMethod === 'pix') {
        navigate(`/cliente/pagamento/pix/${response.data.id}`);
      } else if (paymentMethod === 'card') {
        navigate(`/cliente/pagamento/cartao/${response.data.id}`);
      } else {
        navigate(`/cliente/pedido/${response.data.id}`);
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Erro ao criar pedido');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  if (!storeCart) return null;

  const finalDeliveryFee = deliveryFee - deliveryDiscount;
  const total = subtotal + finalDeliveryFee;
  const isFormValid = customerName.trim().split(' ').length >= 2 && 
                      validateCPF(customerCpf) && 
                      validatePhone(customerPhone) && 
                      cepValidated && 
                      addressNumber.trim();

  return (
    <div className="min-h-screen bg-gray-50 pb-32">
      {/* Header */}
      <header className="bg-[#1A1A1A] text-white shadow-sm sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center gap-4">
          <button
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <FiArrowLeft className="text-xl" />
          </button>
          <h1 className="font-semibold">Finalizar Compra</h1>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-4">
        {error && (
          <div className="p-4 bg-red-100 border border-red-300 text-red-700 rounded-xl">
            {error}
          </div>
        )}

        {/* Store Info */}
        <div className="bg-white rounded-xl p-4 shadow-sm border-l-4 border-[#DC143C]">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="font-semibold text-gray-800 mb-1">{store?.name}</h3>
              <p className="text-sm text-gray-500">{storeCart.items.length} {storeCart.items.length === 1 ? 'item' : 'itens'}</p>
            </div>
            {store?.is_open !== undefined && (
              <span className={`text-xs px-2 py-1 rounded-full ${
                store.is_open 
                  ? 'bg-green-100 text-green-700' 
                  : 'bg-orange-100 text-orange-700'
              }`}>
                {store.is_open ? '🟢 Aberta' : '🟡 Fechada'}
              </span>
            )}
          </div>
        </div>

        {/* Store Closed Warning */}
        {store && !store.is_open && (
          <div className="bg-orange-50 border border-orange-200 rounded-xl p-4">
            <div className="flex items-start gap-3">
              <span className="text-2xl">⏰</span>
              <div>
                <h4 className="font-semibold text-orange-800">Loja fechada no momento</h4>
                <p className="text-sm text-orange-700 mt-1">
                  {store.status_message || 'A loja está fechada agora.'}
                </p>
                <p className="text-sm text-orange-600 mt-2">
                  <strong>Você ainda pode comprar!</strong> Seu pedido será preparado assim que a loja abrir.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Customer Data */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FiUser className="text-[#DC143C]" />
            Dados do Comprador
          </h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Nome Completo *
              </label>
              <input
                type="text"
                value={customerName}
                onChange={(e) => setCustomerName(e.target.value)}
                placeholder="Seu nome completo"
                className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                data-testid="customer-name-input"
              />
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CPF *
                </label>
                <CpfInput
                  value={customerCpf}
                  onChange={setCustomerCpf}
                  error={cpfError}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                  data-testid="customer-cpf-input"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Telefone/WhatsApp *
                </label>
                <PhoneInput
                  value={customerPhone}
                  onChange={setCustomerPhone}
                  error={phoneError}
                  className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                  data-testid="customer-phone-input"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Address with CEP */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FiMapPin className="text-[#DC143C]" />
            Endereço de Entrega
          </h3>
          
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-1">CEP *</label>
            <CepInput onAddressFound={handleAddressFound} />
          </div>

          {cepValidated && (
            <>
              <div className="grid grid-cols-2 gap-3 mb-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Número *</label>
                  <input
                    type="text"
                    value={addressNumber}
                    onChange={(e) => setAddressNumber(e.target.value)}
                    placeholder="Nº"
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                    data-testid="address-number-input"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Complemento</label>
                  <input
                    type="text"
                    value={addressComplement}
                    onChange={(e) => setAddressComplement(e.target.value)}
                    placeholder="Apto, Bloco..."
                    className="w-full p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none"
                    data-testid="address-complement-input"
                  />
                </div>
              </div>

              <div className="bg-green-50 border border-green-200 rounded-xl p-3">
                <p className="text-sm text-green-700">
                  <span className="font-medium">📍 Entregar em:</span>
                </p>
                <p className="text-green-800 mt-1">
                  {deliveryAddress}
                  {addressNumber && `, ${addressNumber}`}
                  {addressComplement && ` - ${addressComplement}`}
                </p>
              </div>
            </>
          )}
        </div>

        {/* Payment Method */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-4 flex items-center gap-2">
            <FiCreditCard className="text-[#DC143C]" />
            Forma de Pagamento
          </h3>
          <div className="space-y-2">
            <button
              type="button"
              onClick={() => setPaymentMethod('pix')}
              className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                paymentMethod === 'pix'
                  ? 'border-[#DC143C] bg-red-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="payment-pix-button"
            >
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <FiDollarSign className="text-green-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-800">PIX</p>
                <p className="text-sm text-gray-500">Pagamento instantâneo</p>
              </div>
              {paymentMethod === 'pix' && (
                <span className="ml-auto text-[#DC143C] text-sm font-medium">Selecionado</span>
              )}
            </button>

            <button
              type="button"
              onClick={() => setPaymentMethod('card')}
              className={`w-full p-4 rounded-xl border-2 flex items-center gap-3 transition-all ${
                paymentMethod === 'card'
                  ? 'border-[#DC143C] bg-red-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid="payment-card-button"
            >
              <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                <FiCreditCard className="text-blue-600" />
              </div>
              <div className="text-left">
                <p className="font-medium text-gray-800">Cartão de Crédito</p>
                <p className="text-sm text-gray-500">Pague com cartão de crédito</p>
              </div>
              {paymentMethod === 'card' && (
                <span className="ml-auto text-[#DC143C] text-sm font-medium">Selecionado</span>
              )}
            </button>
          </div>
        </div>

        {/* Cupom de Frete */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2">
            <FiTag className="text-[#DC143C]" />
            Cupom de Desconto no Frete
          </h3>
          
          {!couponApplied ? (
            <div className="space-y-3">
              <div className="flex gap-2">
                <input
                  type="text"
                  value={couponCode}
                  onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                  placeholder="Digite o código do cupom"
                  className="flex-1 p-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-[#DC143C] focus:border-[#DC143C] outline-none font-mono"
                  data-testid="coupon-input"
                />
                <button
                  onClick={() => validateCoupon()}
                  disabled={couponLoading || !couponCode.trim()}
                  className="px-4 py-3 bg-[#DC143C] text-white rounded-xl hover:bg-[#B01030] disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="apply-coupon-btn"
                >
                  {couponLoading ? '...' : 'Aplicar'}
                </button>
              </div>
              {couponError && (
                <p className="text-sm text-red-600 flex items-center gap-1">
                  <FiX size={14} />
                  {couponError}
                </p>
              )}
            </div>
          ) : (
            <div className="bg-green-50 border border-green-200 rounded-xl p-3">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-green-700 font-medium flex items-center gap-1">
                    <FiCheck />
                    Cupom aplicado!
                  </p>
                  <p className="text-sm text-green-600">
                    <span className="font-mono font-bold">{couponApplied.code}</span>
                    {' '}- {couponApplied.discount_percent}% de desconto no frete
                  </p>
                </div>
                <button
                  onClick={removeCoupon}
                  className="text-red-500 hover:text-red-700 p-2"
                  data-testid="remove-coupon-btn"
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-white rounded-xl p-4 shadow-sm">
          <h3 className="font-semibold text-gray-800 mb-3">Resumo do Pedido</h3>
          <div className="space-y-3 text-sm">
            {storeCart.items.map((item, index) => (
              <div key={`${item.product.id}-${item.selectedColor}-${item.selectedSize}-${index}`} className="flex justify-between items-start border-b pb-2 last:border-0 last:pb-0">
                <div className="flex-1">
                  <span className="text-gray-800 font-medium">
                    {item.quantity}x {item.product.name}
                  </span>
                  {/* Mostrar variações selecionadas */}
                  {(item.selectedColor || item.selectedSize) && (
                    <div className="flex gap-2 mt-1">
                      {item.selectedColor && (
                        <span className="text-xs px-2 py-0.5 bg-blue-100 text-blue-700 rounded-full">
                          🎨 {item.selectedColor}
                        </span>
                      )}
                      {item.selectedSize && (
                        <span className="text-xs px-2 py-0.5 bg-purple-100 text-purple-700 rounded-full">
                          📏 {item.selectedSize}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                <span className="text-gray-800 font-medium ml-2">
                  {formatCurrency(item.product.price * item.quantity)}
                </span>
              </div>
            ))}
            <div className="border-t pt-2 mt-2 space-y-1">
              <div className="flex justify-between">
                <span className="text-gray-600">Subtotal</span>
                <span className="text-gray-800">{formatCurrency(subtotal)}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Taxa de Entrega</span>
                {deliveryDiscount > 0 ? (
                  <div className="text-right">
                    <span className="text-gray-400 line-through text-xs mr-2">
                      {formatCurrency(deliveryFee)}
                    </span>
                    <span className="text-green-600 font-medium">
                      {finalDeliveryFee === 0 ? 'GRÁTIS!' : formatCurrency(finalDeliveryFee)}
                    </span>
                  </div>
                ) : (
                  <span className="text-gray-800">{formatCurrency(deliveryFee)}</span>
                )}
              </div>
              {deliveryDiscount > 0 && (
                <div className="flex justify-between text-green-600">
                  <span className="flex items-center gap-1">
                    <FiTag size={12} />
                    Desconto no frete
                  </span>
                  <span>-{formatCurrency(deliveryDiscount)}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold pt-2 border-t">
                <span className="text-gray-800">Total</span>
                <span className="text-[#DC143C]">{formatCurrency(total)}</span>
              </div>
            </div>
          </div>
        </div>

        {/* Security Notice */}
        <div className="bg-gray-100 rounded-xl p-3 text-center">
          <p className="text-xs text-gray-500">
            🔒 Seus dados estão protegidos. Pagamento processado com segurança pelo Mercado Pago.
          </p>
        </div>
      </main>

      {/* Bottom Bar */}
      <div className="fixed bottom-0 left-0 right-0 bg-white border-t shadow-lg p-4">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between mb-3">
            <span className="text-gray-600">Total a pagar</span>
            <span className="text-2xl font-bold text-[#DC143C]">{formatCurrency(total)}</span>
          </div>
          <button
            onClick={handleSubmit}
            disabled={submitting || !isFormValid}
            className="w-full py-4 bg-[#DC143C] text-white rounded-xl font-semibold hover:bg-[#B01030] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="confirm-order-button"
          >
            {submitting ? (
              <>
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                Processando...
              </>
            ) : (
              'Confirmar Pedido'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Checkout;
